import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    MapPin,
    Navigation,
    User,
    Phone,
    DollarSign,
    StickyNote,
    GripVertical,
    CheckCircle2,
    AlertCircle
} from 'lucide-react';

const SortableAdditionalStopCard = ({ stop, onManage }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: `stop-${stop.id}` });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
    };

    // Paradas adicionales siempre son "draggables" si están en la lista pendiente
    const canDrag = stop.status === 'pending';

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...(canDrag ? { ...attributes, ...listeners } : {})}
            className={`rounded-md shadow-sm border-2 border-purple-200 overflow-hidden transition-all touch-none bg-gradient-to-r from-purple-50 to-white mb-3 ${canDrag ? 'select-none cursor-grab' : ''
                } ${isDragging ? 'scale-[1.01] shadow-lg ring-1 ring-purple-300 z-50 cursor-grabbing' : ''}`}
        >
            <div className="p-2">
                {/* Header Row: Icon + Type + Description + Drag */}
                <div className="flex items-center gap-1.5 mb-1 bg-purple-100/50 px-1.5 py-1 rounded">
                    <div className="w-6 h-6 bg-purple-700 text-white rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0">
                        {stop.sequenceNumber || (stop.stopType === 'pickup' ? '📦' :
                            stop.stopType === 'delivery' ? '🚚' :
                                stop.stopType === 'errand' ? '📝' : '📍')}
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                            <span className="text-[9px] font-black text-purple-700 bg-purple-200 px-1 rounded uppercase">EXTRA</span>
                            <span className="text-[10px] text-gray-900 font-bold truncate">
                                {stop.description || 'Sin descripción'}
                            </span>
                        </div>
                    </div>

                    {canDrag && (
                        <div className="p-1 flex-shrink-0">
                            <GripVertical className="w-4 h-4 text-purple-400" />
                        </div>
                    )}
                </div>

                {/* Info Row: Address + Map Button */}
                <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 min-w-0 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-purple-600 flex-shrink-0" />
                        <span className="text-[11px] text-gray-700 leading-none truncate font-semibold">
                            {stop.address}
                        </span>
                    </div>
                    <div className="flex gap-1">
                        <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stop.address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-purple-600 text-white p-1 rounded"
                        >
                            <Navigation className="w-3.5 h-3.5" />
                        </a>
                    </div>
                </div>

                {/* Details Row: Contact + Notes */}
                <div className="flex items-center justify-between text-[10px] text-gray-600 gap-2 border-t border-purple-100 pt-1">
                    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        {(stop.contactName || stop.contactPhone) && (
                            <div className="flex items-center gap-1 truncate">
                                <User className="w-2.5 h-2.5 text-purple-500" />
                                <span className="truncate">{stop.contactName || stop.contactPhone}</span>
                            </div>
                        )}
                        {stop.paymentAmount > 0 && (
                            <div className="flex items-center gap-1 font-bold text-purple-800">
                                <DollarSign className="w-2.5 h-2.5" />
                                <span>${parseFloat(stop.paymentAmount).toLocaleString('es-CO')}</span>
                            </div>
                        )}
                    </div>
                    {stop.notes && (
                        <div className="max-w-[120px] bg-purple-100/30 px-1.5 py-0.5 rounded text-[9px] italic truncate">
                            {stop.notes}
                        </div>
                    )}
                </div>
            </div>
            {/* Details Row 2: Actions */}
            <div className="flex items-center justify-between gap-2 border-t border-purple-100 pt-1.5 mt-1.5">
                {stop.status === 'completed' ? (
                    <div className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-100">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-black uppercase tracking-wider">Completado</span>
                    </div>
                ) : (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onManage) onManage(stop);
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 active:scale-[0.98] transition-all shadow-md shadow-purple-200"
                    >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-black uppercase tracking-wider">Gestionar</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default SortableAdditionalStopCard;
